import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AddSupportFormReq } from '../../services/support';
import PageShell, { SurfaceCard } from '../../components/common/PageShell';

const SupportForm = () => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    category: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category) {
      toast.error(t('supportForm.categoryRequired'));
      return;
    }
    if (!formData.title.trim()) {
      toast.error(t('supportForm.titleRequired'));
      return;
    }
    if (!formData.message.trim()) {
      toast.error(t('supportForm.messageRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await AddSupportFormReq({
        title: formData.title.trim(),
        text: formData.message.trim(),
        categories: formData.category,
      });
      if (response?.statusCode === 200) {
        toast.success(t('supportForm.success') || response.message);
        navigate(-1);
      } else {
        toast.error(response?.message || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageShell
      maxWidth="sm"
      title={t('supportForm.title')}
      subtitle={t('supportForm.subtitle') || t('settings.support')}
      icon={<SupportAgentIcon />}
    >
      <SurfaceCard sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Box component="form" onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('supportForm.category.label')}</InputLabel>
            <Select
              value={formData.category}
              label={t('supportForm.category.label')}
              onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
            >
              <MenuItem value="tech">{t('supportForm.category.tech')}</MenuItem>
              <MenuItem value="account">{t('supportForm.category.account')}</MenuItem>
              <MenuItem value="general">{t('supportForm.category.general')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="normal"
            required
            fullWidth
            label={t('supportForm.titleField')}
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            rows={5}
            label={t('supportForm.message')}
            value={formData.message}
            onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
          />

          <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
            <Button fullWidth variant="outlined" onClick={() => navigate(-1)} disabled={isLoading}>
              {t('common.back')}
            </Button>
            <Button type="submit" fullWidth variant="contained" disabled={isLoading} sx={{ py: 1.15 }}>
              {isLoading ? <CircularProgress size={22} color="inherit" /> : t('supportForm.submitButton')}
            </Button>
          </Box>
        </Box>
      </SurfaceCard>
    </PageShell>
  );
};

export default SupportForm;
